import { roundHiLoThreshold } from '../../../utils/unitUtils';

function numberOrUndefined(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function hiloThresholdFromPipe(
  pipe: Record<string, unknown> | null | undefined
): number | undefined {
  if (!pipe) return undefined;
  const raw = pipe.hiloThreshold ?? pipe.hilo_threshold ?? pipe.hiloThresholdMm;
  return numberOrUndefined(raw);
}

function mapPipeRotationToPipefitter(value: unknown): string | undefined {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return undefined;
  if (raw.includes('anti')) return 'Anti-Clockwise';
  if (raw.includes('clock')) return 'Clockwise';
  if (raw === 'any') return 'Any';
  return undefined;
}

/**
 * Map setup pipe fields to pipefitter project upsert payload.
 * Expects setup-DB storage units on `pipe`:
 * - nominalPipeLength, targetPipelineLength → ft (no conversion)
 * - hiLoThreshold, nominalOD → mm
 * All numeric fields are coerced to numbers (never strings).
 */
export function buildPipefitterProjectPayloadFromSetupPipe(
  project: {
    projectId?: string | number | null;
    projectNumber?: string | number | null;
    projectName?: string | null;
  },
  pipe: Record<string, unknown>
): Record<string, unknown> {
  const projectId = numberOrUndefined(project.projectId);
  const nominalOD =
    numberOrUndefined(pipe.nominalOd) ??
    numberOrUndefined(pipe.nominalOdMax) ??
    numberOrUndefined(pipe.nominalOdMin);

  const payload: Record<string, unknown> = {
    projectNumber: String(project.projectNumber ?? '').trim(),
    projectName: String(project.projectName ?? '').trim(),
  };

  if (projectId != null) payload.projectId = projectId;

  const weldSeamExclusion = numberOrUndefined(pipe.weldSeamExclusionDeg);
  const exclusion6OClock = numberOrUndefined(pipe.sixOclockExclusionDeg);
  const hiLoThreshold = hiloThresholdFromPipe(pipe);
  const nominalPipeLength = numberOrUndefined(pipe.nominalPipeLength);
  const targetNumberOfPipes = numberOrUndefined(pipe.targetNumberOfPipes);
  const targetPipelineLength = numberOrUndefined(pipe.targetPipelineLength);
  const pipeRotation = mapPipeRotationToPipefitter(pipe.pipeRotation);
  const pipeMaterial = String(pipe.pipeMaterial ?? '').trim();

  // Lengths: ft only (numbers, no conversion)
  if (nominalPipeLength != null) payload.nominalPipeLength = nominalPipeLength;
  if (targetPipelineLength != null) payload.targetPipelineLength = targetPipelineLength;

  // HiLo: mm only, 2 decimals (e.g. 1.50, 2.00)
  if (hiLoThreshold != null) payload.hiLoThreshold = roundHiLoThreshold(hiLoThreshold);

  // Dimensionless / mm OD: numbers only
  if (nominalOD != null) payload.nominalOD = nominalOD;
  if (weldSeamExclusion != null) payload.weldSeamExclusion = weldSeamExclusion;
  if (exclusion6OClock != null) payload.exclusion6OClock = exclusion6OClock;
  if (targetNumberOfPipes != null) payload.targetNumberOfPipes = targetNumberOfPipes;

  if (pipeMaterial) payload.pipeMaterial = pipeMaterial;
  if (pipeRotation) payload.pipeRotation = pipeRotation;

  return payload;
}
